import React, { Component } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { translate } from "react-i18next"
import uniqid from "uniqid"
import ReactTooltip from "react-tooltip"
import { connect } from "react-redux"
import { compose } from "redux"
import { toast } from "react-toastify"
import { ENVIRONMENT } from "../../../constants"
import { media } from "../../../utils/style-utils"
import { downloadFile } from "../../../utils/file-utils"
import {
  convertFilterStateToUrlParams,
  convertFilterStateToPostLocations
} from "../../../utils/filter-utils"
import WidgetExportDownloadNotification from "../../layout/widget/WidgetExportDownloadNotification" // eslint-disable-line
import WidgetExportDownloadCompleteNotification from "../../layout/widget/WidgetExportDownloadCompleteNotification" // eslint-disable-line
import WidgetExportDownloadErrorNotification from "../../layout/widget/WidgetExportDownloadErrorNotification" // eslint-disable-line

const WidgetMenuContainer = styled.div`
  text-align: ${props => (props.position === "left" ? "left" : "right")};
  margin: ${props => (props.position === "left" ? "-8px 0 0 5px" : "-8px 5px 0 0")};
`

const WidgetTitle = styled.h5`
  text-align: center;
`

const WidgetSubtitle = styled.p`
  text-align: center;
  font-size: small;
`

const WidgetHeaderIconContainer = styled.div`
  display: inline-block;
  position: relative;
`

const WidgetHeaderIcon = styled.a`
  display: none;
  padding: 5px;
  cursor: default;
  ${media.phone`cursor: pointer;`};
`

const WidgetHeaderIconButton = WidgetHeaderIcon.extend`
  &:hover {
    color: ${props => props.primaryColor} !important;
    cursor: pointer;
  }
`

const FilterTooltipContainer = styled.div`
  text-align: left;
`

const FilterList = styled.ul`
  padding-left: 15px;
  margin: 3px 0 5px 0;
`

const FilterCountBadge = styled.span`
  font-family: Arial;
  background-color: #fff;
  display: inline-block;
  width: 12px;
  height: 12px;
  font-size: 10px;
  line-height: 10px;
  color: #99abb4;
  border: solid 1px #ccc;
  padding: 1px;
  text-align: center;
  border-radius: 50%;
  position: absolute;
  top: -5px;
  right: -5px;
  cursor: default;
`

class WidgetHeader extends Component {
  static propTypes = {
    /** The api url to download the export from */
    exportEndpoint: PropTypes.string.isRequired,
    /** The title shown at the top of the widget */
    titleKey: PropTypes.string.isRequired,
    /** Optional subtitle */
    subtitleKey: PropTypes.string,
    t: PropTypes.func.isRequired,
    appliedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
    /* Extra parameters to tag on the end of the export URL */
    exportParams: PropTypes.string
  }

  componentWillMount = () => {
    // Create a unique id for the tooltips to use
    const uniqueId = uniqid()
    this.excelTooltipId = `${uniqueId}_xt`
    this.filterTooltipId = `${uniqueId}_ft`
    this.toastId = null
  }

  getFiltersAsQueryString = () => {
    return convertFilterStateToUrlParams(this.props.filterState)
  }

  getFilterTranslationIdentifier = filterName => {
    switch (filterName) {
      case "StartDate":
      case "EndDate":
      case "Review":
      case "AppraisalLevels":
      case "Locations":
      case "DataMode":
      case "ViewMode":
        return `Filters.Standard.${filterName}.Name`
      case "Generation":
      case "Gender":
      case "JobTitles":
      case "Manager":
        return `Filters.Advanced.${filterName}.Name`
      default:
        return filterName
    }
  }

  downloadExportFile = () => {
    let apiUrl = this.props.exportEndpoint
    const extraExportParams = this.props.exportParams
    if (apiUrl) {
      apiUrl = ENVIRONMENT.getApiRootUrl() + apiUrl + this.getFiltersAsQueryString()

      let locations = []
      // Get array of location ids for PUT to database if any selected
      if (
        this.props.filterState.fields.locations &&
        this.props.filterState.fields.locations.selectedValue
      ) {
        locations = convertFilterStateToPostLocations(this.props.filterState)
      }

      // Add the user's current culture code to the querystring
      apiUrl = `${apiUrl}&cultureCode=${this.props.userDetails.culture}`

      if (extraExportParams) {
        apiUrl = `${apiUrl}${extraExportParams}`
      }

      this.toastId = null

      const onStart = () => {
        this.toastId = toast(<WidgetExportDownloadNotification />, {
          hideProgressBar: true,
          className: "download-notification",
          autoClose: false
        })
      }

      const onDownloadComplete = () => {
        toast.update(this.toastId, {
          render: <WidgetExportDownloadCompleteNotification />,
          autoClose: 3500
        })
      }

      const onFail = () => {
        toast.update(this.toastId, {
          render: <WidgetExportDownloadErrorNotification />,
          autoClose: 3500
        })
      }

      downloadFile(apiUrl, locations, this.props.access_token, onStart, onDownloadComplete, onFail)
    }
  }

  render() {
    const { t, titleKey, subtitleKey, appliedFilters, userDetails } = this.props

    const appliedFilterCount =
      appliedFilters && appliedFilters.length > 0 ? appliedFilters.length : 0

    let filterDescription
    if (appliedFilterCount > 0) {
      filterDescription = (
        <FilterTooltipContainer>
          {t("Widgets.Shared.Filtering.TooltipIntro")}
          <FilterList>
            {appliedFilters.map(f => <li key={f}>{t(this.getFilterTranslationIdentifier(f))}</li>)}
          </FilterList>
        </FilterTooltipContainer>
      )
    } else {
      filterDescription = t("Widgets.Shared.Filtering.NoFiltersApplied")
    }

    return (
      <div className="row">
        <div className="col-md-1 d-none d-md-block p-0">
          <WidgetMenuContainer position="left">
            <WidgetHeaderIconContainer>
              <WidgetHeaderIcon
                className="show-on-card-hover"
                data-tip
                data-for={this.filterTooltipId}
                data-offset="{'bottom': 7, 'right': 10}"
              >
                <i className="fa fa-filter" />
                {appliedFilterCount > 0 && (
                  <FilterCountBadge>{appliedFilterCount}</FilterCountBadge>
                )}
              </WidgetHeaderIcon>
              <ReactTooltip id={this.filterTooltipId} effect="solid">
                {filterDescription}
              </ReactTooltip>
            </WidgetHeaderIconContainer>
          </WidgetMenuContainer>
        </div>
        <div className="col-12 col-md-10 p-0 text-center">
          <WidgetTitle>{t(titleKey)}</WidgetTitle>
          <WidgetSubtitle>{t(subtitleKey)}&nbsp;</WidgetSubtitle>
        </div>
        <div className="col-md-1 p-0 text-right d-print-none d-none d-md-block">
          <WidgetMenuContainer position="right">
            <WidgetHeaderIconContainer>
              <WidgetHeaderIconButton
                className="show-on-card-hover"
                data-tip
                data-for={this.excelTooltipId}
                data-offset="{'bottom': 7, 'right': 10}"
                onClick={this.downloadExportFile}
                primaryColor={userDetails.theme.primaryColor}
              >
                <i className="fa fa-file-excel-o" />
              </WidgetHeaderIconButton>
              <ReactTooltip id={this.excelTooltipId} effect="solid">
                {t("Widgets.Shared.ExportToExcelTooltip")}
              </ReactTooltip>
            </WidgetHeaderIconContainer>
          </WidgetMenuContainer>
        </div>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    filterState: state.filters,
    userDetails: state.userDetail,
    access_token: state.oidc.user.access_token
  }
}

export default compose(translate(), connect(mapStateToProps))(WidgetHeader)



// WEBPACK FOOTER //
// ./src/components/layout/widget/WidgetHeader.jsx